import React, { forwardRef, HTMLAttributes, useEffect, useRef } from "react";
import classnames from "classnames";

import { delay } from "@formant/core/common/delay";

import { animate } from "../../utils/animate";
import { useTicker } from "../../utils/useTicker";
import styles from "./Fader.module.scss";

interface IFaderProps {
    visible: boolean;
    hasClosed?: () => void;
    slow?: boolean;
    visibleClassName?: string;
    children?: React.ReactNode;
}

export const Fader = forwardRef<
    HTMLDivElement,
    IFaderProps & HTMLAttributes<HTMLDivElement>
>(function Fader(props, ref) {
    const {
        visible,
        slow,
        className,
        visibleClassName,
        children,
        hasClosed,
        ...rest
    } = props;

    const totallyVisible = useRef(visible);
    const [, tock] = useTicker();

    useEffect(() => {
        let cancelled = false;
        let done = false;

        async function update() {
            if (!cancelled) {
                if (visible && !totallyVisible.current) {
                    await delay();
                    totallyVisible.current = true;
                    tock();
                } else if (!visible && totallyVisible.current) {
                    await animate();
                    totallyVisible.current = false;
                    tock();
                    hasClosed?.();
                }
            }
        }

        void update().then(() => (done = true));

        return () => {
            if (!done) {
                cancelled = true;
            }
        };
    }, [visible, totallyVisible, tock]);

    const isInvisible = !visible && !totallyVisible.current;
    const isVisible = visible && totallyVisible.current;

    if (isInvisible) {
        return null;
    }

    return (
        <div
            ref={ref}
            className={classnames(
                styles.fader,
                className,
                slow && styles.slow,
                isVisible && styles.visible,
                isVisible && visibleClassName,
            )}
            {...rest}
        >
            {children}
        </div>
    );
});
